import AppIcon from "common/app-icon";
import type { Icon } from "common/components/icons";
import type { ActionResult} from "common/components/types";
import { LOADING } from "common/components/types";
import type { UseWeather} from "hooks/use-weather";
import { unitsTemperatureText } from "hooks/use-weather";
import * as React from "react";
import { useTheme } from "styled-components";
import type { RuntimeTheme } from "webapp/lib/theme/core-themes/runtime-theme";

import * as Style from "./weather-style";

interface WeatherProps {
    hideCity: boolean;
    onClick?: () => Promise<ActionResult | undefined>;
    weather: UseWeather;
}

const getIconForOWM = (icon: string | undefined): Icon => {
    // icons ending in "d" are daytime
    // icons ending in "n" are nighttime
    switch (icon) {
        case "01d":
            return "34-03-weather-sun";
        case "01n":
            return "34-03-weather-night-clear";
        case "02d":
            return "34-03-weather-cloud";
        case "02n":
            return "34-03-weather-night-cloudy";
        case "03d":
            return "34-03-weather-cloud-1";
        case "03n":
            return "34-03-weather-night-cloudy";
        case "04d":
            return "34-03-weather-clouds";
        case "04n":
            return "34-03-weather-night-cloudy";
        case "09d":
            return "34-03-weather-cloud-rain";
        case "09n":
            return "34-03-weather-night-rain";
        case "10d":
            return "34-03-weather-rain-drops";
        case "10n":
            return "34-03-weather-night-rain-drops";
        case "11d":
            return "34-03-weather-cloud-thunder";
        case "11n":
            return "34-03-weather-night-thunder-rain";
        // these two are for mist, which we don't have.
        case "13d":
            return "34-03-weather-rain-drops";
        case "13n":
            return "34-03-weather-night-rain-drops";
        default:
            return "";
    }
};

export const Weather: React.FunctionComponent<WeatherProps> = p => {
    const { hideCity, onClick, weather: weatherHook } = p;
    const { units, weather } = weatherHook;
    const theme: RuntimeTheme = useTheme() as RuntimeTheme;

    const icon = React.useMemo(() => {
        return getIconForOWM(weather?.weather[0]?.icon);
    }, [weather]);

    const unitText = React.useMemo(() => unitsTemperatureText(units), [units]);

    if (weather === LOADING) {
        return (
            <Style.Wrapper>
                <Style.TextWrapper>
                    <Style.TextSub>Loading weather...</Style.TextSub>
                </Style.TextWrapper>
            </Style.Wrapper>
        );
    }

    if (weather === undefined) {
        return (
            <Style.Wrapper>
                <Style.TextWrapper>
                    <Style.TextSub>Could not get the weather.</Style.TextSub>
                </Style.TextWrapper>
            </Style.Wrapper>
        );
    }

    const temp = weather.main.temp.toFixed(0);
    const name = weather.weather[0].description;
    const mainText = `${temp}${unitText} ${name}`;

    if (hideCity) {
        return (
            <Style.Wrapper onClick={onClick}>
                <Style.IconWrapper>
                    <AppIcon icon={icon ?? ""} size={48} color={theme.fgColorDark} />
                </Style.IconWrapper>
                <Style.TextWrapper>
                    <Style.TextMain>{mainText}</Style.TextMain>
                </Style.TextWrapper>
            </Style.Wrapper>
        );
    } else {
        const city = weather.name;
        const country = weather.sys.country;
        const subText = `${city}, ${country}`;
        return (
            <Style.Wrapper onClick={onClick}>
                <Style.IconWrapper>
                    <AppIcon icon={icon ?? ""} size={48} color={theme.fgColorDark} />
                </Style.IconWrapper>
                <Style.TextWrapper>
                    <Style.TextMain>{mainText}</Style.TextMain>
                    <Style.TextSub>{subText}</Style.TextSub>
                </Style.TextWrapper>
            </Style.Wrapper>
        );
    }
};
